import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Play, Pause, ChevronRight, ChevronLeft, ArrowLeft, 
  Users, Eye, Lock, CheckCircle2, Shield, 
  Building2, Star, Clock, Handshake, Home, ExternalLink
} from 'lucide-react';
import AEDIcon from '../components/icons/AEDIcon';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';

const InvestorDemo = () => {
  const navigate = useNavigate();
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  const slides = [
    {
      id: 'intro',
      title: 'Welcome to Rashii',
      subtitle: 'Decision Intelligence for Real Estate',
      content: 'Rashii is a verified exchange platform where smart buyers meet serious sellers. Let\'s walk through the platform experience.',
      icon: Home,
      action: null,
      duration: 8000
    },
    {
      id: 'landing',
      title: 'The Landing Page',
      subtitle: 'First Impressions Matter',
      content: 'Users arrive at rashii.ae and immediately see our value proposition: a verified exchange with decision intelligence. Clear CTAs guide them to register or explore properties.',
      icon: Home,
      action: { label: 'View Landing Page', path: '/' },
      duration: 10000
    },
    {
      id: 'register',
      title: 'User Registration',
      subtitle: 'Building Trust from Day One',
      content: 'Registration is simple but thorough. Users choose their role (Buyer, Seller, or Agent) and provide basic information. This is the first step in our verification process.',
      icon: Users,
      action: { label: 'Try Registration', path: '/register' },
      duration: 10000
    },
    {
      id: 'kyc',
      title: 'KYC Verification',
      subtitle: 'Everyone is Verified',
      content: 'Before accessing the platform, all users complete KYC. For sellers, this includes Emirates ID verification and property ownership documents. This ensures only legitimate participants.',
      icon: Shield,
      action: null,
      duration: 10000
    },
    {
      id: 'properties',
      title: 'Property Discovery',
      subtitle: 'Intelligent Search',
      content: 'Buyers browse verified listings with full transparency. Each property shows the Rashii Score - an AI-generated analysis of price fairness, location value, and market timing.',
      icon: Building2,
      action: { label: 'Browse Properties', path: '/properties' },
      duration: 10000
    },
    {
      id: 'rashii-score',
      title: 'The Rashii Score',
      subtitle: 'Data-Driven Decisions',
      content: 'Our proprietary algorithm analyzes 50+ factors including comparable sales, area development plans, price history, and market trends. Buyers get confidence; sellers get fair pricing.',
      icon: Star,
      action: null,
      duration: 10000
    },
    {
      id: 'intent',
      title: 'Intent Commitment',
      subtitle: 'Serious Buyers Only',
      content: 'Before viewing a property, buyers must commit intent. This small commitment (refundable if they proceed) filters out casual browsers and ensures sellers only meet serious prospects.',
      icon: Lock,
      action: null,
      duration: 10000
    },
    {
      id: 'viewing',
      title: 'Viewing Management',
      subtitle: 'Controlled Access',
      content: 'Viewings are limited and scheduled. Each property has a maximum number of viewing slots, creating healthy urgency. Sellers can review buyer profiles before accepting viewings.',
      icon: Eye,
      action: null,
      duration: 10000
    },
    {
      id: 'offers',
      title: 'Structured Offers',
      subtitle: 'Transparent Negotiation',
      content: 'Offers are time-bound with clear terms. Counter-offers follow a structured format. No more ghosting, endless back-and-forth, or emotional negotiations.',
      icon: AEDIcon,
      action: null,
      duration: 10000
    },
    {
      id: 'settlement',
      title: 'Mandatory Settlement',
      subtitle: 'Guaranteed Completion',
      content: 'All accepted offers settle through Rashii escrow. Funds are held securely until all conditions are met. Built-in dispute resolution handles edge cases.',
      icon: Handshake,
      action: null,
      duration: 10000
    },
    {
      id: 'dashboards',
      title: 'Role-Based Dashboards',
      subtitle: 'Everything in One Place',
      content: 'Buyers track their journey from search to ownership. Sellers manage listings, viewings, and offers. Agents coordinate multiple clients. Admins oversee the entire ecosystem.',
      icon: CheckCircle2,
      action: { label: 'View Demo Dashboard', path: '/login' },
      duration: 10000
    },
    {
      id: 'conclusion',
      title: 'The Future of Real Estate',
      subtitle: 'Join the Revolution',
      content: 'Rashii is building the trust layer for real estate transactions. Verified participants, intelligent pricing, structured negotiation, and guaranteed settlement. This is how real estate should work.',
      icon: Star,
      action: { label: 'Back to Investor Page', path: '/investor' },
      duration: 10000
    }
  ];

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setProgress(prev => {
          if (prev >= 100) {
            // Move to next slide
            if (currentSlide < slides.length - 1) {
              setCurrentSlide(curr => curr + 1);
              return 0;
            } else {
              setIsPlaying(false);
              return 100;
            }
          }
          return prev + (100 / (slides[currentSlide].duration / 100));
        });
      }, 100);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentSlide, slides]);

  useEffect(() => {
    setProgress(0);
  }, [currentSlide]);

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(curr => curr + 1);
      setProgress(0);
    }
  };

  const handlePrev = () => {
    if (currentSlide > 0) {
      setCurrentSlide(curr => curr - 1);
      setProgress(0);
    }
  };

  const togglePlay = () => {
    setIsPlaying(!isPlaying);
  };

  const currentSlideData = slides[currentSlide];
  const Icon = currentSlideData.icon;

  return (
    <div className="min-h-screen bg-background" data-testid="investor-demo">
      <div className="noise-overlay" />
      
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl border-b border-white/10 bg-background/80">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/investor')} className="text-muted-foreground">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Deck
            </Button>
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded logo-icon-metallic flex items-center justify-center">
                <span className="font-display text-sm text-primary-foreground font-bold">R</span>
              </div>
              <span className="font-display text-lg gold-interactive">Rashii</span>
              <Badge variant="gold" className="ml-2">PLATFORM DEMO</Badge>
            </div>
            <div className="text-sm text-muted-foreground">
              {currentSlide + 1} / {slides.length}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-6 py-12">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex gap-1 mb-2">
            {slides.map((slide, idx) => (
              <div 
                key={slide.id}
                className={`h-1 flex-1 rounded-full cursor-pointer transition-colors ${
                  idx < currentSlide ? 'bg-primary' : 
                  idx === currentSlide ? 'bg-primary/50' : 'bg-secondary'
                }`}
                onClick={() => {
                  setCurrentSlide(idx);
                  setProgress(0);
                }}
              />
            ))}
          </div>
          <Progress value={progress} className="h-0.5" />
        </div>

        {/* Slide Content */}
        <Card className="glass-card border-white/10 mb-8">
          <CardContent className="p-12">
            <div className="flex flex-col items-center text-center max-w-2xl mx-auto">
              {/* Icon */}
              <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mb-6">
                <Icon className="w-10 h-10 text-primary" />
              </div>
              
              {/* Title */}
              <Badge variant="gold" className="mb-4">{currentSlideData.subtitle}</Badge>
              <h2 className="font-display text-4xl mb-4">{currentSlideData.title}</h2>
              
              {/* Content */}
              <p className="text-lg text-muted-foreground leading-relaxed mb-8">
                {currentSlideData.content}
              </p>
              
              {/* Action Button */}
              {currentSlideData.action && (
                <Button 
                  onClick={() => window.open(currentSlideData.action.path, '_blank')}
                  className="btn-gold-metallic"
                >
                  {currentSlideData.action.label}
                  <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Controls */}
        <div className="flex items-center justify-center gap-4">
          <Button 
            variant="outline" 
            size="lg"
            onClick={handlePrev}
            disabled={currentSlide === 0}
            className="border-white/20"
          >
            <ChevronLeft className="w-5 h-5 mr-2" /> Previous
          </Button>
          
          <Button 
            size="lg"
            onClick={togglePlay}
            className={isPlaying ? 'bg-primary' : 'btn-gold-metallic'}
          >
            {isPlaying ? (
              <>
                <Pause className="w-5 h-5 mr-2" /> Pause
              </>
            ) : (
              <>
                <Play className="w-5 h-5 mr-2" /> {currentSlide === 0 ? 'Start Tour' : 'Resume'}
              </>
            )}
          </Button>
          
          <Button 
            variant="outline" 
            size="lg"
            onClick={handleNext}
            disabled={currentSlide === slides.length - 1}
            className="border-white/20"
          >
            Next <ChevronRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        {/* Slide Thumbnails */}
        <div className="mt-12">
          <h3 className="text-sm text-muted-foreground mb-4 text-center">All Sections</h3>
          <div className="grid grid-cols-6 gap-3">
            {slides.map((slide, idx) => {
              const SlideIcon = slide.icon;
              return (
                <button
                  key={slide.id}
                  onClick={() => {
                    setCurrentSlide(idx);
                    setProgress(0);
                    setIsPlaying(false);
                  }}
                  className={`p-3 rounded-lg border transition-all text-center ${
                    idx === currentSlide 
                      ? 'border-primary bg-primary/10' 
                      : 'border-white/10 hover:border-white/30'
                  }`}
                >
                  <SlideIcon className={`w-5 h-5 mx-auto mb-2 ${
                    idx === currentSlide ? 'text-primary' : 'text-muted-foreground'
                  }`} />
                  <p className="text-xs text-muted-foreground truncate">{slide.title}</p>
                </button>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
};

export default InvestorDemo;
